import { useEffect, useRef, type ReactNode } from "react";
import { Button } from "./primitives";

export interface EmptyStateProps {
  art: string;
  title: string;
  body?: ReactNode;
  /** 빈 상태에서는 다음 행동을 생략하지 않는다. */
  action?: ReactNode;
  hint?: string;
  tone?: "neutral" | "bad";
}

export function EmptyState({ art, title, body, action, hint, tone = "neutral" }: EmptyStateProps) {
  return (
    <div className={`blank${tone === "bad" ? " bad" : ""}`}>
      <span className="art" aria-hidden="true">
        {art}
      </span>
      <span className="h">{title}</span>
      {body ? <span className="b">{body}</span> : null}
      {action}
      {hint ? (
        <span className="b" style={{ fontSize: 11 }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/** 막지 않는 통보. 무엇이 바뀌었고 그 결과 숫자가 어떻게 됐는지까지 한 줄에 쓴다. */
export function Toast({ message }: { message: string }) {
  return (
    <div className="toastwrap" role="status" aria-live="polite">
      <span className="toast">
        <span className="tk" aria-hidden="true">
          ✓
        </span>
        <span>{message}</span>
      </span>
    </div>
  );
}

export interface ConfirmDialogProps {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** 되돌릴 수 없는 동작에만 쓴다. 무엇이 함께 사라지는지를 버튼 위에서 말한다. */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  cancelLabel = "그대로 두기",
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    opener.current = document.activeElement;
    ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
    // 닫으면 열었던 컨트롤로 포커스를 되돌린다.
    return () => {
      (opener.current as HTMLElement | null)?.focus?.();
    };
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onCancel();
      if (event.key !== "Tab" || !ref.current) return;
      const focusable = ref.current.querySelectorAll<HTMLElement>("button, [href], input, [tabindex]");
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <span className="st">{title}</span>
        {/* 줄글이라 어절 보존을 걷는다(`styles/base.css` 의 `.prose`). 지금 부르는 자리 둘
            (`screens/History.tsx` 의 되돌리기·지우기 확인)이 세 문장 98자·79자다. ⛔ 한 문장짜리
            짧은 `body` 를 넣게 되면 이 칸을 떼라. 이 자리는 클래스가 아니라 글이 정한다. */}
        <span className="sb prose">{body}</span>
        <div className="row">
          <Button label={cancelLabel} variant="ghost" onClick={onCancel} />
          <Button label={confirmLabel} variant={destructive ? "danger" : "primary"} onClick={onConfirm} />
        </div>
      </div>
    </div>
  );
}
